import {
  AR_OPTIONAL_FEATURES,
  AR_REQUIRED_FEATURES,
  ARButton,
  FloorPlane,
  HitTestTarget,
  PlacementAnchor,
  SceneUnderstanding,
} from "./xr-reality";
import {
  BoxGeometry,
  BufferGeometry,
  Mesh,
  MeshBasicMaterial,
  PerspectiveCamera,
  Scene,
  SphereGeometry,
  Vector3,
  WebGLRenderer,
  Line,
  LineBasicMaterial,
  MeshPhysicalMaterial,
  // Frustum,
  // Matrix4,
  // AxesHelper,
} from "three";

import { XRControllerModelFactory } from "three/examples/jsm/webxr/XRControllerModelFactory.js";
import { XRHandModelFactory } from "three/examples/jsm/webxr/XRHandModelFactory.js";
import { AssetManager } from "./AssetManager";
import { Button } from "./button";
import * as THREE from "three";
import { ContentUI } from "./content-ui";
import { HotspotKiosk, KioskContent } from "./hotspot-kiosk";
import { BasicSpot } from "./basic-spot";
import { EngineAudio } from "./EngineAudio";
import { LollipopHelper } from "./lollipop-helper";
import { UNICPanel, UNICState } from "./UNIC-panel";
import { degToRad, radToDeg } from "three/src/math/MathUtils.js";
import * as ThreeMeshUI from "three-mesh-ui";
import { EasingFunction } from "./easing-utils";
import { ArrowSpot } from "./arrow-spot";
import { addDepthOverride } from "./material-utils";

interface PendingAnchorData {
  position: Vector3;
  quaternion: THREE.Quaternion;
}

export enum EngineType {
  W25,
  W31
}

/**
 * Places the engine in the room with WebXR floor planes, hit-testing, and one anchor.
 */
export class RATKApp {
  private _clock = new THREE.Clock(true);

  private _fontPathBold = "./NotoSans-Bold-msdf.json";
  private _fontTexturePathBold = "./NotoSans-Bold.png";


  private camera!: PerspectiveCamera;
  private _scene: Scene;
  private _renderer!: WebGLRenderer;
  private reality!: SceneUnderstanding;
  private pendingAnchorData: PendingAnchorData | null = null;

  // Hit testing properties
  private hitTestMarker!: THREE.Mesh;
  private hitTestLine!: THREE.Line;

  private _lastHitTestTarget: THREE.Group = new THREE.Group();
  // private _lastHitTestTarget = new AxesHelper(1);

  private controllerModelFactory!: XRControllerModelFactory;
  private handModelFactory!: XRHandModelFactory;
  private primaryController!: any; // XR controller
  private secondaryController!: any; // Right XR controller
  private connectedControllers = new Set<any>(); // Track actual connected controllers
  private _activeHitTestController: any = null; // Track which controller is actively doing hit testing

  // Single persistent anchor management
  private currentAnchor: PlacementAnchor | null = null;
  // private currentMarker: THREE.Mesh | null = null;

  // Grip rotation tracking
  private _rotationOffset: number = 0; // Cumulative Y-axis rotation from grip and thumbstick
  private _rightInputSource: XRInputSource | null = null;
  private readonly _thumbstickDeadzone = 0.2;
  private readonly _thumbstickYawSpeed = degToRad(90);
  private _baseRotation: THREE.Euler = new THREE.Euler(); // Store base rotation before grip adjustments

  private _pendingHeightOffset: number = 0;
  private _pendingHeightOffsetSpeed: number = 1;

  private _lowestPlane?: FloorPlane;
  private _planes: FloorPlane[] = [];
  // private _hotspots: Map<string, Hotspot> = new Map();
  private _hotspots: Map<string, BasicSpot> = new Map();
  private _hotspotKiosk!: HotspotKiosk;

  private _elevatorHotspot?: ArrowSpot;
  // private _elevatorHotspot = new BasicSpot();
  // private _elevatorDownHotspot = new BasicSpot();

  private _elevatorName = "W31_Lowered";
  private _elevatorTarget?: THREE.Object3D;
  private _elevatorTraverse = 2;
  private _elevatorSpeed = 0.5;
  private _elevatorPhase = 0;
  private _elevatorDirection = -1;
  private _elevatorStartY = 0;

  private _audioListener = new THREE.AudioListener();
  private _engineAudio!: EngineAudio;

  // a bit dirty fix to get 2 audio sources in a pinch
  private _lidAudio!: EngineAudio;
  private _unicPanel!: UNICPanel;
  private _flywheel: THREE.Object3D | null = null;
  private _flywheelSpeed: number = 0;
  private _flywheelSpeedMultiplier: number = 1;

  private set lowestPlane(plane: FloorPlane) {
    if (this.lowestPlane !== undefined) {
      (this.lowestPlane.planeMesh.material as THREE.MeshBasicMaterial).visible =
        false;
    }
    (plane.planeMesh.material as THREE.MeshBasicMaterial).visible = true;
    this._lowestPlane = plane;
  }

  private get lowestPlane(): FloorPlane | undefined {
    return this._lowestPlane;
  }

  // XR Camera tracking for proper forward direction
  private _xrCameras: {
    leftCamera?: THREE.PerspectiveCamera;
    rightCamera?: THREE.PerspectiveCamera;
    midPoint: THREE.Vector3;
  } = { midPoint: new THREE.Vector3() };

  // GLB Model properties
  private _floorRoot = new THREE.Group();
  // private _floorRoot = new THREE.AxesHelper(1);
  // private _floorRoot = new THREE.Mesh(new THREE.SphereGeometry(0.1), new THREE.MeshBasicMaterial({color: 0xffff00}));
  private _floorPointer = new THREE.Mesh(
    new THREE.SphereGeometry(0.1),
    new THREE.MeshBasicMaterial({
      color: 0x00ff00,
      transparent: true,
      opacity: 0.25,
    })
  );
  private _mainModel?: THREE.Object3D | null;
  private _assetManager: AssetManager;

  private _hotboxHatch = {
    target: undefined as THREE.Object3D | undefined,
    state: 'closed' as 'closed' | 'open',
    animation: {
      closedRotation: 0,
      openRotation: degToRad(-80),
      phase: 0,
      duration: 2,
      easing: EasingFunction.easeInOutQuad,
    }
  }

  private _transparentIndicatorMaterial: THREE.MeshBasicMaterial =
    new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.35,
      alphaTest: 0.1,
      side: THREE.FrontSide,
      // visible: false,
    });

  // Button properties

  // this is used to track if the user is touching a button
  private _buttons: Button[] = [];
  private _controllers: THREE.Object3D[] = [];

  // HelperUI properties
  private _helperUI: ContentUI;
  private _uiVisible: boolean = true;
  private _uiDistance: number = 0.5; // Distance in front of user
  // private _uiLeftOffset: number = -0.3; // Offset to the left from center
  private _uiLeftOffset: number = 0.2; // Offset to the left from center
  private _headLevelY?: number; // Store the head level Y position
  private _xrSession: XRSession | null = null; // check this for null to see if session is active

  // Preview mode properties
  private _previewMode: boolean = true;
  private _previewGroup: THREE.Group = new THREE.Group();
  private _modelRotationSpeed: number = 0.3; // Rotation speed in radians per second

  private _autoExitOnTrackingLoss: boolean = true; // Set to false to manually handle tracking loss
  // private _autoExitOnTrackingLoss: boolean = false; // Set to false to manually handle tracking loss

  // constructor(private _engineType: EngineType) {
  constructor() {
    console.log("RATKApp constructor starting...");

    // Initialize AssetManager
    this._assetManager = new AssetManager();
    // this.checkWebXRSupport();
    // Check WebXR support
    // this.checkWebXRSupport();

    this._scene = new THREE.Scene();
    this._renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this._renderer.setPixelRatio(window.devicePixelRatio);
    this._renderer.setSize(window.innerWidth, window.innerHeight);
    this._renderer.xr.enabled = true;
    // Immersive AR on Quest grants local-floor. bounded-floor rejects the session.
    this._renderer.xr.setReferenceSpaceType("local-floor");
    // this._renderer.xr.setReferenceSpaceType("local");
    this._renderer.outputColorSpace = THREE.SRGBColorSpace;
    this._renderer.toneMapping = THREE.ACESFilmicToneMapping;

    // Add lights for preview mode
    // const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    // this._scene.add(ambientLight);

    // const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    // directionalLight.position.set(5, 5, 5);
    // this._scene.add(directionalLight);

    // const light = new THREE.HemisphereLight(0xffffff, 0xbbbbff, 3);
    // light.position.set(0.5, 1, 0.25);
    // this._scene.add(light);

    // Configure renderer for proper transparency rendering
    // this._renderer.autoClear = false; // Disable auto-clear for better transparency control
    this.camera = new THREE.PerspectiveCamera(
      70,
      window.innerWidth / window.innerHeight,
      0.01,
      20
    );

    // Set up preview camera position
    this.setupPreviewCamera();

    this._renderer.setAnimationLoop(() => this.update());
    // this._renderer.xr.setAnimationLoop(() => this.update());
    document.body.appendChild(this._renderer.domElement);

    // Add window resize handler
    window.addEventListener('resize', () => this.onWindowResize());

    this.setupARButtons();
    this.setupHitTestMarkers();
    this.setupController();
    this.setupHandTracking();
    this.setupSceneUnderstanding();

    
    // Check AR support and update UI accordingly (delayed to allow AR button to initialize)
    setTimeout(() => this.checkARSupport(), 100);

    // Initialize HelperUI
    this._helperUI = new ContentUI();
    this._helperUI.scale.setScalar(0.25);
    this._scene.add(this._helperUI);
    this._helperUI.setContent("None");
    // this._helperUI.setContent('NoOffset');

    // Add floor root and pointer to scene
    this._floorPointer.visible = true; // Initially hidden
    // this._scene.add(this._floorPointer);

    this._floorRoot.visible = true;
    // this._scene.add(this._floorRoot);

    // Add preview group to scene for landing page preview
    this._scene.add(this._previewGroup);

    // Load material database first, then GLB models
    this.initializeAssets();

    // Add buttons
    // this.addButtons();

    // origin marker
    // this._scene.add(new Mesh(new BoxGeometry(0.1, 0.3, 0.1), new MeshBasicMaterial({color: 0x0000ff})));
    this._scene.add(this._lastHitTestTarget);

    // const audioLolli = new LollipopHelper();
    // this._audioListener.add(audioLolli);
    // audioLolli.position.set(0, 0, 0);
    this._scene.add(this._audioListener);
  }

  private addHotspots(): void {
    const openSound = this._assetManager.getAudioAsset('click-graphic-open')!
    const closeSound = this._assetManager.getAudioAsset('click-graphic-close')!
    
    this._mainModel?.traverse((child) => {

      if (child.name === "W31_Flywheel"){
        this._flywheel = child;
      }

      // if (false) { // disabled for now
      if (child.name === "Lift_Arrows") {

        this._elevatorTarget = this._mainModel?.getObjectByName(this._elevatorName);
        this._elevatorStartY = this._elevatorTarget?.position.z ?? 0;


        this._elevatorHotspot = new ArrowSpot(child);
        this._elevatorHotspot.scale.setScalar(0.05);
        child.add(this._elevatorHotspot);
        // this._elevatorHotspot.position.copy(child.position);
        this._elevatorHotspot.addToggleOnSound(openSound, this._audioListener);
        this._elevatorHotspot.addToggleOffSound(closeSound, this._audioListener);
        this._hotspots.set("elevator", this._elevatorHotspot);
        this._buttons.push(this._elevatorHotspot);
        // this._elevatorHotspot.add(new THREE.AxesHelper(5));

        this._elevatorHotspot.buttonAction = () => {
          if (this._elevatorDirection < 0) {
            this._elevatorHotspot?.playAnimation('up')
            this._elevatorDirection = 1;
          } else if (this._elevatorDirection) {
            this._elevatorDirection = -1;
            this._elevatorHotspot?.playAnimation('down')
          }
        }
      }

      if (child.name === "W31_Covers_Front_Ctrl") {
        this._hotboxHatch.target = child;
      }

      if (child.name.includes("hotspot")) {
        // this idea was discarded for now
        if (child.name.includes("_content")) {
          // console.log("content", child.name);
        } else {
          const hotspot = new BasicSpot();
          hotspot.scale.setScalar(0.05);
          child.add(hotspot);
          this._hotspots.set(child.name, hotspot);
          this._buttons.push(hotspot);
          
          // const openSound = this._assetManager.getAudioAsset('click-graphic-open')!
          // const closeSound = this._assetManager.getAudioAsset('click-graphic-close')!
          hotspot.addToggleOnSound(openSound, this._audioListener)
          hotspot.addToggleOffSound(closeSound, this._audioListener)
          
          // console.log("hotspot", child.name);
          // hide the gas initially, diesel is default
          if (child.name === "hotspot_5"){
            hotspot.visible = false;
          }
          
          // console.log("hotspot", child.name);
          hotspot.buttonAction = () => {
            if (this._xrCameras.leftCamera) {
              this._hotspots.forEach((hotspot) => {
                hotspot.hoverAnimation.targeted = false;
              });

              let matchingContent = KioskContent.W25_HotSpot_1;

              switch (child.name) {
                case "hotspot_1":
                  matchingContent = KioskContent.W25_HotSpot_1;
                  break;
                case "hotspot_2":
                  matchingContent = KioskContent.W25_HotSpot_2;
                  break;
                case "hotspot_3":
                  matchingContent = KioskContent.W25_HotSpot_3;
                  break;
                case "hotspot_4":
                  matchingContent = KioskContent.W25_HotSpot_4;
                  break;
                case "hotspot_5":
                  matchingContent = KioskContent.W25_HotSpot_5;
                  break;
                case "hotspot_6":
                  matchingContent = KioskContent.W25_HotSpot_6;
                  break;

                // somthing special here
                case "hotspot_7":
                  switch (this._unicPanel.fuelVariant) {
                    case "diesel":
                      matchingContent = KioskContent.W25_HotSpot_7_diesel;
                      break;
                    case "lng":
                      matchingContent = KioskContent.W25_HotSpot_7_df;
                      break;
                    case "ammonia":
                      matchingContent = KioskContent.W25_HotSpot_7_ammonia;
                      break;
                  }
                  break;
                // case "hotspot_7":
                //   matchingContent = KioskContent.HotSpot7_diesel;
                //   break;
                // case "hotspot_7":
                //   matchingContent = KioskContent.HotSpot7_methanol;
                //   break;
                case "hotspot_8":
                  matchingContent = KioskContent.W25_HotSpot_8;
                  break;

                // W31
                case "hotspot_3_W31":
                  matchingContent = KioskContent.W31_HotSpot_3;
                  break;
                case "hotspot_4_W31":
                  matchingContent = KioskContent.W31_HotSpot_4;
                  break;
                case "hotspot_6_W31":
                  matchingContent = KioskContent.W31_HotSpot_6;
                  break;

                case "hotspot_8_W31":
                  matchingContent = KioskContent.W31_HotSpot_8;
                  break;

                case "hotspot_11_W31":
                case "hotspot_11_W31_1":
                  matchingContent = KioskContent.W31_HotSpot_11;
                  break;
                case "hotspot_12_W31":
                  switch (this._unicPanel.fuelVariant) {
                    case "diesel":
                      matchingContent = KioskContent.W31_HotSpot_12_diesel;
                      break;
                    case "lng":
                      matchingContent = KioskContent.W31_HotSpot_12_df;
                      break;
                    case "ammonia":
                      matchingContent = KioskContent.W31_HotSpot_12_df;
                      break;
                  }
                  break;
                case "hotspot_13_W31":
                  matchingContent = KioskContent.W31_HotSpot_13;
                  break;
                case "hotspot_14_W31":
                  matchingContent = KioskContent.W31_HotSpot_14;
                  break;
                // case "hotspot_anim_covers_W31":
                //   matchingContent = KioskContent.W31_HotSpot_Anim_Covers;
              }

              // special case for the anim covers hotspot
              if (child.name === "hotspot_anim_covers_W31") {
                if (this._hotboxHatch.animation.phase > 0 && this._hotboxHatch.animation.phase < 1) {
                  return;
                }
                this._hotspotKiosk.showKioskContent(
                  this._xrCameras,
                  KioskContent.None
                );
                if (this._hotboxHatch.state === 'open') {
                  this._lidAudio.playAudio('lid-close');
                  this._hotboxHatch.state = 'closed';
                } else {
                  this._lidAudio.playAudio('lid-open');
                  this._hotboxHatch.state = 'open';
                }
                hotspot.hoverAnimation.targeted = this._hotboxHatch.state === 'open';
                hotspot.playToggleSound(hotspot.hoverAnimation.targeted);
                return;
              }

              // selected new content
              if (this._hotspotKiosk.currentContent !== matchingContent) {
                this._hotspotKiosk.showKioskContent(
                  this._xrCameras,
                  matchingContent
                );
                hotspot.hoverAnimation.targeted = true;
                hotspot.playToggleSound(true);
              } else { // close the current content

                this._hotspotKiosk.showKioskContent(
                  this._xrCameras,
                  KioskContent.None
                );
                hotspot.hoverAnimation.targeted = false;
                hotspot.playToggleSound(false);
                
              }
            }
          };
        }
      }
    });
  }
  /**
   * Setup preview camera for landing page
   */
  private setupPreviewCamera(): void {
    // Position camera to view the model
    this.camera.position.set(0, 0, 7);
    this.camera.lookAt(0, 0, 0);
  }

  /**
   * Handle window resize
   */
  private onWindowResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this._renderer.setSize(window.innerWidth, window.innerHeight);
  }

  /**
   * Check AR support and update UI accordingly
   * Checks for both immersive-ar support and all required features
   */
  private async checkARSupport(): Promise<void> {
    const usageInstructions = document.getElementById("usage-instructions");
    const infoDiv = document.getElementById("info");
    const infoTexts = infoDiv?.querySelectorAll("p");
    const appContainer = document.getElementById("app");

    if (!navigator.xr) {
      console.warn("WebXR not supported");
      // Add class to align content to top
      if (appContainer) {
        appContainer.classList.add("no-ar-support");
      }
      // Hide usage instructions
      if (usageInstructions) {
        usageInstructions.style.display = "none";
      }
      // Update info text - replace both paragraphs with a single message
      if (infoTexts && infoTexts.length > 0) {
        infoTexts[0].innerHTML = "WebXR is not supported on this device.<br>Please use a VR headset with a compatible browser.";
        // Hide the second paragraph if it exists
        if (infoTexts[1]) {
          infoTexts[1].style.display = "none";
        }
      }
      // Update AR button to redirect to portfolio
      this.setupPortfolioRedirect();
      return;
    }

    try {
      // First check basic immersive-ar support
      const basicSupport = await navigator.xr.isSessionSupported("immersive-ar");

      if (!basicSupport) {
        console.warn("Immersive AR not supported");
        // Add class to align content to top
        if (appContainer) {
          appContainer.classList.add("no-ar-support");
        }
        // Hide usage instructions
        if (usageInstructions) {
          usageInstructions.style.display = "none";
        }
        // Update info text
        if (infoTexts && infoTexts.length > 0) {
          infoTexts[0].innerHTML = "AR is not supported on this device.<br>Please use a VR headset (Meta Quest, HTC Vive, etc.) with a compatible browser.";
          if (infoTexts[1]) {
            infoTexts[1].style.display = "none";
          }
        }
        // Update AR button to redirect to portfolio
        this.setupPortfolioRedirect();
        return;
      }

      // Check if required features are supported using supportsSession (safer than requestSession)
      // Note: supportsSession is not available in all browsers, so we'll use a different approach

      // For now, we'll use a heuristic based on the user agent and known device capabilities
      // This is safer than requesting actual sessions which require user gestures

      // Check if we can at least verify basic feature support
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobile = /mobile|android|iphone|ipad|ipod/.test(userAgent);
      const isQuest = /quest/.test(userAgent);

      // Check if the AR button was disabled by ARButton.convertToARButton
      const arButton = document.getElementById("ar-button") as HTMLButtonElement;
      const buttonDisabled = arButton?.disabled || arButton?.style.display === "none";

      // Meta Quest devices support all features, mobile devices may not support hand-tracking
      if (buttonDisabled || (isMobile && !isQuest)) {
        if (buttonDisabled) {
          console.warn("AR button disabled - required features not supported");
        } else {
          console.warn("Mobile device detected - some advanced AR features may not be available");
        }

        // Add class to align content - mobile to bottom, others to top
        if (appContainer) {
          if (isMobile && !isQuest) {
            appContainer.classList.add("mobile-no-ar-support");
          } else {
            appContainer.classList.add("no-ar-support");
          }
        }
        // Hide usage instructions
        if (usageInstructions) {
          usageInstructions.style.display = "none";
        }
        // Update info text
        if (infoTexts && infoTexts.length > 0) {
          const message = buttonDisabled
            ? `Required AR features are not available on this device.`
            : `Mobile AR has limited feature support.`;
          infoTexts[0].innerHTML = `${message}<br><small>This experience requires hand tracking, plane detection, and other advanced AR features.</small><br>Please use a VR headset (Meta Quest, HTC Vive, etc.) with full AR capabilities.`;
          if (infoTexts[1]) {
            infoTexts[1].style.display = "none";
          }
        }
        // Update AR button to redirect to portfolio
        this.setupPortfolioRedirect();
      } else {
        // console.log("✅ Immersive AR with required features appears to be supported");
      }
    } catch (error) {
      console.error("Error checking AR support:", error);
      // Add class to align content to top
      if (appContainer) {
        appContainer.classList.add("no-ar-support");
      }
      // Hide usage instructions on error
      if (usageInstructions) {
        usageInstructions.style.display = "none";
      }
      // Update info text
      if (infoTexts && infoTexts.length > 0) {
        infoTexts[0].innerHTML = "Unable to check AR support.<br>Please use a VR headset with a compatible browser.";
        if (infoTexts[1]) {
          infoTexts[1].style.display = "none";
        }
      }
      // Update AR button to redirect to portfolio
      this.setupPortfolioRedirect();
    }
  }

  /**
   * Setup portfolio redirect when AR is not supported
   */
  private setupPortfolioRedirect(): void {
    const portfolioButton = document.getElementById("portfolio-button") as HTMLButtonElement;
    const w25Button = document.getElementById("W25-button") as HTMLButtonElement;
    const w31Button = document.getElementById("W31-button") as HTMLButtonElement;

    console.log("Setting up portfolio redirect");
    w25Button.hidden = true;
    w31Button.hidden = true;
    w31Button.style.display = "none";
    w25Button.style.display = "none";
    portfolioButton.style.display = "block";

    // portfolioButton.parentNode?.replaceChild(newButton, arButton);

    // Add click listener to redirect to portfolio
    portfolioButton.addEventListener("click", () => {
      // TODO: Replace with actual portfolio URL
      const portfolioUrl = "https://wartsila-marine-portfolio.com/view/5279557d-790d-4663-8d05-3ec6248efd15";
      window.open(portfolioUrl, "_blank");
    });

    // console.log("AR button configured for portfolio redirect");
  }

  private stopSoundEffects(): void {
    this._engineAudio?.stopAudio();
    this._lidAudio?.stopAudio();
    for (const button of this._buttons) {
      button.stopSelectSound();
    }
    for (const hotspot of this._hotspots.values()) {
      hotspot.stopToggleSounds();
    }
  }

  private setupARButtons(): void {
    const portfolioButton = document.getElementById("portfolio-button") as HTMLButtonElement;
    portfolioButton.hidden = true;
    portfolioButton.style.display = "none";
    const w25Button = document.getElementById("W25-button") as HTMLButtonElement;
    w25Button.disabled = true;
    w25Button.addEventListener("click", () => {
      console.log("W25 button clicked");
      this.loadGLBModels("./global/W25_DF_Genset_boolean.glb", "./global/W25_Placement.glb").then(() => {
        this.assignInteractionElements('W25');
      });
    });
    const w31Button = document.getElementById("W31-button") as HTMLButtonElement;
    w31Button.disabled = true;
    w31Button.addEventListener("click", () => {
      console.log("W31 button clicked");
      this.loadGLBModels("./global/W31.glb", "./global/W31_Placement.glb").then(() => {
        this.assignInteractionElements("W31");
      });
    });

    // const arButton = document.getElementById("ar-button") as HTMLButtonElement;
    ARButton.convertToARButton(w25Button, this._renderer, {
      requiredFeatures: AR_REQUIRED_FEATURES,
      optionalFeatures: AR_OPTIONAL_FEATURES,
      ENTER_XR_TEXT: "Enter W25",
      LEAVE_XR_TEXT: "Exit W25",
      XR_NOT_ALLOWED_TEXT: "XR not allowed",
      XR_NOT_SUPPORTED_TEXT: "XR not supported",
      onUnsupported() {
        // console.log("XR not supported");
        // w25Button.hidden = true;
        // w31Button.hidden = true;
        // portfolioButton.hidden = false;
      },
    });
    ARButton.convertToARButton(w31Button, this._renderer, {
      requiredFeatures: AR_REQUIRED_FEATURES,
      optionalFeatures: AR_OPTIONAL_FEATURES,
      ENTER_XR_TEXT: "Enter W31",
      LEAVE_XR_TEXT: "Exit W31",
      XR_NOT_ALLOWED_TEXT: "XR not allowed",
      XR_NOT_SUPPORTED_TEXT: "XR not supported",
    });
  }

  /**
   * Setup hit testing markers and lines
   */
  private setupHitTestMarkers(): void {
    // Create hit test marker (sphere that shows where controller is pointing)
    const markerGeometry = new SphereGeometry(0.02, 32, 32);
    const markerMaterial = new MeshBasicMaterial({
      color: 0xff0000,
      transparent: true,
      opacity: 0.8,
    });
    this.hitTestMarker = new Mesh(markerGeometry, markerMaterial);
    this.hitTestMarker.visible = false;
    this._scene.add(this.hitTestMarker);
    this.hitTestMarker.add(this._floorPointer);

    // Create line from controller to hit point
    const lineGeometry = new BufferGeometry().setFromPoints([
      new Vector3(0, 0, 0),
      new Vector3(0, 0, -1),
    ]);
    const lineMaterial = new LineBasicMaterial({
      color: 0x0000ff,
      transparent: true,
      opacity: 0.6,
    });

    this.hitTestLine = new Line(lineGeometry, lineMaterial);
    this.hitTestLine.visible = false;
    this._scene.add(this.hitTestLine);
  }

  /**
   * Setup XR controller and hit testing (both left and right controllers)
   */
  private setupController(): void {
    this.controllerModelFactory = new XRControllerModelFactory();

    // Setup controller 0
    this.primaryController = this._renderer.xr.getController(0);
    this.primaryController.addEventListener(
      "connected",
      this.handleControllerConnected.bind(this)
    );
    this.primaryController.addEventListener(
      "disconnected",
      this.handleControllerDisconnected.bind(this)
    );
    this.primaryController.addEventListener(
      "selectstart",
      this.handleSelectStart.bind(this)
    );
    this.primaryController.addEventListener(
      "selectend",
      this.handleSelectEnd.bind(this)
    );
    this.primaryController.addEventListener(
      "squeezestart",
      this.handleSqueezeStart.bind(this)
    );
    this.primaryController.addEventListener(
      "squeezeend",
      this.handleSqueezeEnd.bind(this)
    );
    this._scene.add(this.primaryController);

    const primaryControllerGrip = this._renderer.xr.getControllerGrip(0);
    primaryControllerGrip.add(
      this.controllerModelFactory.createControllerModel(primaryControllerGrip)
    );
    this._scene.add(primaryControllerGrip);

    // Setup controller 1
    this.secondaryController = this._renderer.xr.getController(1);
    this.secondaryController.addEventListener(
      "connected",
      this.handleControllerConnected.bind(this)
    );
    this.secondaryController.addEventListener(
      "disconnected",
      this.handleControllerDisconnected.bind(this)
    );
    this.secondaryController.addEventListener(
      "selectstart",
      this.handleSelectStart.bind(this)
    );
    this.secondaryController.addEventListener(
      "selectend",
      this.handleSelectEnd.bind(this)
    );
    // this.secondaryController.addEventListener(
    //   "select",
    //   this.handleSelectStart.bind(this)
    // );

    this.secondaryController.addEventListener(
      "squeezestart",
      this.handleSqueezeStart.bind(this)
    );
    this.secondaryController.addEventListener(
      "squeezeend",
      this.handleSqueezeEnd.bind(this)
    );
    this._scene.add(this.secondaryController);

    const secondaryControllerGrip = this._renderer.xr.getControllerGrip(1);
    secondaryControllerGrip.add(
      this.controllerModelFactory.createControllerModel(secondaryControllerGrip)
    );
    this._scene.add(secondaryControllerGrip);
  }

  /**
   * Setup hand tracking for general interactions (not for cube placement)
   */
  private setupHandTracking(): void {
    this.handModelFactory = new XRHandModelFactory();

    // Setup hand tracking for left hand
    const leftHand = this._renderer.xr.getHand(0);
    leftHand.addEventListener("connected", () => {
      // console.log("Left hand connected");
      const handModel = this.handModelFactory.createHandModel(leftHand, "mesh");
      leftHand.add(handModel);
      this.enableHandTracking(leftHand, "left");
    });
    leftHand.addEventListener("disconnected", () => {
      // console.log("Left hand disconnected");
      this.disableHandTracking(leftHand, "left");
    });
    this._scene.add(leftHand);

    // Setup hand tracking for right hand
    const rightHand = this._renderer.xr.getHand(1);
    rightHand.addEventListener("connected", () => {
      // console.log("Right hand connected");
      const handModel = this.handModelFactory.createHandModel(
        rightHand,
        "mesh"
      );
      rightHand.add(handModel);
      this.enableHandTracking(rightHand, "right");
    });
    rightHand.addEventListener("disconnected", () => {
      // console.log("Right hand disconnected");
      this.disableHandTracking(rightHand, "right");
    });
    this._scene.add(rightHand);
  }

  /**
   * Enable hand tracking and add finger joints to controllers array
   */
  private enableHandTracking(hand: THREE.Group, handedness: string): void {
    // console.log(`🤲 Enabling hand tracking for ${handedness} hand`);

    // The XRHandModelFactory should automatically handle joint tracking
    if (hand.userData) {
      hand.userData.handSpace = hand;
    }

    const joints = (hand as any).joints;
    // console.log("🔍 Joints", joints);

    if ((hand as any).joints !== undefined) {
      // Add index finger tip from both hands for button interactions (left-handed and right-handed support)
      const indexFingerTip = joints["index-finger-tip"];
      if (indexFingerTip && !this._controllers.includes(indexFingerTip)) {
        this._controllers.push(indexFingerTip);
        // console.log(
        // `✅ Added ${handedness} index finger to _controllers array for button interaction`
        // );
      }
    }

    // Ensure the hand model is properly connected to the XR hand tracking
    hand.traverse((child) => {
      if (child.userData) {
        child.userData.handSpace = hand;
      }
    });
  }

  /**
   * Disable hand tracking and remove finger joints from controllers array
   */
  private disableHandTracking(hand: THREE.Group, handedness: string): void {
    // console.log(`🤲 Disabling hand tracking for ${handedness} hand`);

    const joints = (hand as any).joints;
    if (joints !== undefined) {
      // Remove index finger tip from both hands (left-handed and right-handed support)
      const indexFingerTip = joints["index-finger-tip"];
      if (indexFingerTip) {
        const index = this._controllers.indexOf(indexFingerTip);
        if (index > -1) {
          this._controllers.splice(index, 1);
          // console.log(
          // `✅ Removed ${handedness} index finger from _controllers array`
          // );
        }
      }
    }
  }

  /**
   * Setup reference space monitoring for tracking boundary issues
   */
  private setupReferenceSpaceMonitoring(): void {
    if (!this._xrSession) {
      console.warn("Cannot set up reference space monitoring - no active session");
      return;
    }

    const session = this._xrSession;

    // Listen for visibility state changes (when user leaves boundary)
    session.addEventListener('visibilitychange', () => {
      if (session.visibilityState === 'visible-blurred') {
        console.warn("XR Session BLURRED - user may be approaching tracking boundary");
      } else if (session.visibilityState === 'hidden') {
        console.warn("XR Session HIDDEN - user has left tracking area!");

        if (this._autoExitOnTrackingLoss) {

          // End session after a short delay
          setTimeout(() => {
            if (session) {
              session.end().catch((error: any) => {
                console.error("Error ending session:", error);
              });
            }
          }, 500);
        }
      } else if (session.visibilityState === 'visible') {
        console.log("✅ XR Session VISIBLE - tracking active");
        // if (this._helperUI && !this._trackingResetDetected) {
        //   this._helperUI.setContent("None");
        // }
      }
    });

    // console.log("✅ Reference space monitoring enabled");
    // console.log(`   Auto-exit on tracking loss: ${this._autoExitOnTrackingLoss ? 'ENABLED' : 'DISABLED'}`);
  }

  /**
   * Floor planes, controller hit-tests, and the placement anchor.
   */
  private setupSceneUnderstanding(): void {
    try {
      this.reality = new SceneUnderstanding(this._renderer.xr);
      this.reality.onPlaneAdded = this.handlePlaneAdded.bind(this);
      this.reality.onPlaneRemoved = this.handlePlaneRemoved.bind(this);
      this._scene.add(this.reality.root);

      // Listen for session start to set up persistent anchors
      this._renderer.xr.addEventListener("sessionstart", () => {
        // console.log(
        //   "XR Session started - hit testing will be available after scan"
        // );

        // Disable preview mode when entering AR
        this._previewMode = false;
        this._previewGroup.visible = false;
        document.body.classList.add('xr-session');
        // console.log("Preview mode disabled - entering AR mode");

        // Set up XR session tracking
        this._headLevelY = undefined; // Reset head level for recalibration
        this._xrSession = this._renderer.xr.getSession();

        // Set up reference space reset handling
        this.setupReferenceSpaceMonitoring();

        // Detect XR cameras after session start
        this.detectXRCameras();

        // Set up single anchor restoration with visual markers after a delay
        setTimeout(() => {
          this.reality
            .restorePersistentAnchors()
            .then(() => {
              const anchors = Array.from(this.reality.anchors.values());
              if (anchors.length > 0) {
                const anchor = anchors[anchors.length - 1];
                this.currentAnchor = anchor;
                anchor.add(this._floorRoot);
                this.applySavedPlacementOffset(anchor.anchorID);
                this._floorRoot.visible = true;
              } else {
                console.log("No persistent anchors to restore");
              }
            })
            .catch((error) => {
              console.error("Error restoring anchors:", error);
            });
        }, 2000); 
        // TODO: see if we can do this without a timeout, find out what we are waiting for and how to do it without a timeout
        // 1500ms is the minimum timeout to ensure anchors are restored, but 2000ms is more reliable
      });

      // Listen for session end to clean up UI
      this._renderer.xr.addEventListener("sessionend", () => {
        console.log("XR Session ended - restoring landing page UI");
        this.stopSoundEffects();
        this._helperUI.visible = false;
        this._headLevelY = undefined; // Clear head level
        // Clear camera references
        this._xrCameras.leftCamera = undefined;
        this._xrCameras.rightCamera = undefined;
        this._xrSession = null;

        // Re-enable preview mode when exiting AR
        this._previewMode = true;
        this._previewGroup.visible = true;

        this._floorRoot.visible = false;
        this.currentAnchor = null;
        this._planes = [];
        this._lowestPlane = undefined;

        document.body.classList.remove('xr-session');

        // Explicitly restore UI elements visibility
        const infoElement = document.getElementById('info');
        const usageElement = document.getElementById('usage-instructions');
        const modelLoadingElement = document.getElementById('model-loading');
        const appContainer = document.getElementById('app');
        const arButton = document.getElementById('ar-button') as HTMLButtonElement;

        // Force display of info element
        if (infoElement) {
          infoElement.style.removeProperty('display');
          infoElement.style.visibility = 'visible';
          infoElement.style.opacity = '1';
          console.log("✅ Info element restored");
        }

        // Only restore usage instructions if AR is supported
        // Check if app has no-ar-support or mobile-no-ar-support class
        const hasARSupport = appContainer &&
          !appContainer.classList.contains('no-ar-support') &&
          !appContainer.classList.contains('mobile-no-ar-support');

        if (usageElement) {
          if (hasARSupport) {
            usageElement.style.removeProperty('display');
            usageElement.style.visibility = 'visible';
            usageElement.style.opacity = '1';
            console.log("✅ Usage instructions restored");
          } else {
            usageElement.style.display = 'none';
            console.log("ℹ️ Usage instructions remain hidden (AR not supported)");
          }
        }

        if (modelLoadingElement) {
          modelLoadingElement.style.display = 'none';
        }

        // Reset AR button to initial state
        if (arButton && hasARSupport) {
          arButton.style.removeProperty('display');
          arButton.style.visibility = 'visible';
          console.log("✅ AR button restored");
        }

        console.log("✅ Preview mode re-enabled - landing page UI restored");
      });

      // this._renderer.xr.getReferenceSpace()

      // this._xrSession?.requestReferenceSpace("bounded-floor").then((referenceSpace) => {
      //   console.log("Reference space:", referenceSpace);
      //   referenceSpace.addEventListener('reset', () => {
      //     console.log("Reference space reset");
      //   });
      // });

    } catch (error) {
      console.error("Error setting up scene understanding:", error);
    }
  }

  /**
   * Initialize assets by loading material database first, then GLB models
   */
  private async initializeAssets(): Promise<void> {
    try {
      // Load material database first
      // console.log("🔄 Initializing assets...");
      await this._assetManager.loadMaterialDatabase();

      let mainModelPath = "";
      let pointerModelPath = "";

      // load W25 for preview
      mainModelPath = "./global/W25_DF_Genset_boolean.glb";
      pointerModelPath = "./global/W25_Placement.glb";

      
      // mainModelPath = "./global/W31.glb";
      // pointerModelPath = "./global/W25_Placement.glb"; 

      // switch (this._engineType) {
      //   case EngineType.W25:
      //     mainModelPath = "./global/W25_DF_Genset_boolean.glb";
      //     pointerModelPath = "./global/W25_Placement.glb";
      //     break;
      //   case EngineType.W31:
      //     mainModelPath = "./global/W31.glb";
      //     pointerModelPath = "./global/W25_Placement.glb"; 
      //     // pointerModelPath = "./global/W31_Placement.glb"; // not available yet
      //     break;
      // }

      // Then load GLB models
      await this.loadGLBModels(mainModelPath, pointerModelPath);

      // setTimeout(() => {
      //   console.log("Loading W31...");
      //   this.loadGLBModels("./global/W31.glb", "./global/W25_Placement.glb");

      // setTimeout(() => {
      //   console.log("Loading W31...");
      //   this.loadGLBModels(mainModelPath, pointerModelPath);
      // }, 5000);
      // }, 5000);

      // Set scene environment from AssetManager (critical for metallic materials)
      const envTexture = this._assetManager.getEnvironmentTexture();
      if (envTexture) {
        this._scene.environment = envTexture;
        // console.log('✅ Scene environment map set - metallic materials will now reflect properly');
      } else {
        // console.warn('⚠️ No environment texture available - metallic materials may appear flat');
      }

      await this._assetManager.loadAudioAssets();

      this._engineAudio = new EngineAudio(
        this._audioListener,
        this._assetManager
      );

      this._lidAudio = new EngineAudio(
        this._audioListener,
        this._assetManager
      );

      // this.assignInteractionElements();
    const w25Button = document.getElementById("W25-button") as HTMLButtonElement;
    const w31Button = document.getElementById("W31-button") as HTMLButtonElement;
    w25Button.disabled = false;
    w31Button.disabled = false;
    // Hide loading indicator
    const loadingElement = document.getElementById('model-loading');
    if (loadingElement) {
      loadingElement.style.display = 'none';
    }

      // does the rotation order matter?
      // container.rotateOnAxis(new THREE.Vector3(0, 1, 0), Math.PI / 2);

    } catch (error) {
      console.error("❌ Failed to initialize assets:", error);
    }
  }

  private assignInteractionElements(engineType: 'W25' | 'W31') {

    // this._scene.add(this._engineAudio);
    let dieselVariant: THREE.Object3D | undefined = undefined;
    let lngVariant: THREE.Object3D | undefined = undefined;
    let ammoniaVariant: THREE.Object3D | undefined = undefined;
    let w31GasSystem: THREE.Object3D | undefined = undefined;

    let indicatorArrow: THREE.Object3D | undefined = undefined;
    let startButton: THREE.Object3D | undefined = undefined;
    let stopButton: THREE.Object3D | undefined = undefined;
    let loadButton: THREE.Object3D | undefined = undefined;
    let fuelButton: THREE.Object3D | undefined = undefined;

    let emergencyStopButton: THREE.Object3D | undefined = undefined;

    this._mainModel?.traverse((child) => {
      switch (child.name) {

        // these are the physical signs and pipes different fuels
        case "W25_DF":
          dieselVariant = child;
          break;
        case "W25_LNG":
          lngVariant = child;
          break;
        case "W25_Ammonia":
          ammoniaVariant = child;
          break;

        case "W31_Gas_System":
          w31GasSystem = child;
          break;

        case "W25_GUI_Arrow":
          indicatorArrow = child;
          // console.log("Indicator arrow found");
          break;
        case "W25_GUI_Button_Start":
          startButton = child;
          // console.log("Start button found");
          break;
        case "W25_GUI_Button_Stop":
          stopButton = child;
          // console.log("Stop button found");
          break;
        case "W25_GUI_Button_Load":
          loadButton = child;
          // console.log("Load button found");
          break;
        case "W25_GUI_Button_Fuel":
          fuelButton = child;
          // console.log("Fuel button found");
          break;
        case "W25_Emergency_Button_Ctrl":
          emergencyStopButton = child;
          // console.log("Emergency stop button found");
          break;

        // map the W31 buttons only
        case "W31_GUI_Button_Start":
          startButton = child;
          break;
        case "W31_GUI_Button_Stop":
          stopButton = child;
          break;
        case "W31_GUI_Button_Load":
          loadButton = child;
          break;
        case "W31_GUI_Button_Fuel":
          fuelButton = child;
          break;
        case "W31_Emergency_Button_Ctrl":
          emergencyStopButton = child;
          break;
        case "W31_GUI_Arrow":
          indicatorArrow = child;
          break;
      }
    });

    const buttonAudio = this._assetManager.getAudioAsset(
      "click-physical-button"
    )!;
    // if (!startButton || !stopButton || !loadButton || !fuelButton || !emergencyStopButton || !dieselVariant || !lngVariant || !ammoniaVariant || !indicatorArrow) {
    //   console.error("Could not find all interaction elements, cannot create UNICPanel");
    //   console.error("startButton", startButton);
    //   console.error("stopButton", stopButton);
    //   console.error("loadButton", loadButton);
    //   console.error("fuelButton", fuelButton);
    //   console.error("emergencyStopButton", emergencyStopButton);
    //   console.error("dieselVariant", dieselVariant);
    //   console.error("lngVariant", lngVariant);
    //   console.error("ammoniaVariant", ammoniaVariant);
    //   console.error("indicatorArrow", indicatorArrow);
    //   return;
    // }
    this._unicPanel = new UNICPanel(
      this._engineAudio,
      {
        startButton: startButton!,
        stopButton: stopButton!,
        loadButton: loadButton!,
        fuelButton: fuelButton!,
        emergencyStopButton: emergencyStopButton!,
      },
      { dieselModel: dieselVariant!, lngModel: lngVariant!, ammoniaModel: ammoniaVariant! },
      buttonAudio,
      this._audioListener,
      indicatorArrow!,
      engineType
    );
    this._unicPanel.onFuelChange = (variant: "diesel" | "lng" | "ammonia") => {

      //TODO: link W31 unic panel and fuel hotspots too

      if (w31GasSystem) {
        w31GasSystem.visible = variant === "diesel" ? false : true;
        console.log("w31GasSystem", variant, w31GasSystem.visible);
      }


      // always change the gas system 3D for W25
      const gasSystem = this._mainModel?.getObjectByName('W25DF_Gas_System');
      const gasHotspot = this._hotspots.get('hotspot_5')
      if (gasSystem && gasHotspot){
        gasSystem.visible =  variant === "diesel" ? false : true;
        gasHotspot.visible = variant === "diesel" ? false : true;
      }
      let kioskContent: KioskContent = KioskContent.None;
      if (
        this._hotspotKiosk.currentContent !== KioskContent.W25_HotSpot_7_ammonia &&
        this._hotspotKiosk.currentContent !== KioskContent.W25_HotSpot_7_diesel &&
        this._hotspotKiosk.currentContent !== KioskContent.W25_HotSpot_7_df
      ) {
        return;
      }
      switch (variant) {
        case "diesel":
          kioskContent = KioskContent.W25_HotSpot_7_diesel;
          break;
        case "lng":
          kioskContent = KioskContent.W25_HotSpot_7_df;
          break;
        case "ammonia":
          kioskContent = KioskContent.W25_HotSpot_7_ammonia;
          break;

        default:
          break;
      }
      this._hotspotKiosk.showKioskContent(this._xrCameras, kioskContent, true);
    }

    this._unicPanel.onEmergencyStop = () => {
      this._hotspotKiosk.showKioskContent(this._xrCameras, KioskContent.W25_EmergencyStop);

      setTimeout(() => {

        if (this._hotspotKiosk.currentContent === KioskContent.W25_EmergencyStop) {
          this._hotspotKiosk.showKioskContent(this._xrCameras, KioskContent.None);
        }
      }, 1000 * 10);
    }

    this._unicPanel.onButtonPressed = (button: Button) => {
      // console.log("button pressed", button.name);
      if (button.name)

      if (this._hotspotKiosk.currentContent === KioskContent.W25_EmergencyStop) {
        this._hotspotKiosk.showKioskContent(this._xrCameras, KioskContent.None);
      }
    }

    this._unicPanel.onSpeedChange = (newSpeed: number) => {
      // console.log("speed changed", newSpeed);
      this._flywheelSpeed = newSpeed;
    }

    this._mainModel?.add(this._unicPanel);
    this._mainModel?.add(this._engineAudio);
    this._mainModel?.add(this._lidAudio);
    this._engineAudio.position.set(0, 2, 0);
    this._lidAudio.position.set(0, 2, 0);

    if (engineType === 'W25') {
      // this._mainModel?.add(this._unicPanel);
      this._unicPanel.position.set(0, 1.715, 0);
    } else if (engineType === 'W31') {
      // this._unicPanel.position.set(-0.382, 3.715, -3.041);
      this._unicPanel.position.set(-0.379, 3.716, -3.040);
      this._unicPanel.position.y -= 2.1
      this._unicPanel.rotation.set(0, 0, THREE.MathUtils.degToRad(46.50));
      this._unicPanel.emergencyStopButton.position.set(0.02, 0.17, 0.005);
      // this._unicPanel.add(new THREE.AxesHelper(100))
      
      const guiMonitor = this._mainModel?.getObjectByName('W31_GUI_Monitor')
      guiMonitor?.attach(this._unicPanel);
      // this._unicPanel.position.set(-0.382, 3.715, 3.718);
    }
    this._buttons.push(...this._unicPanel.buttons);

    this._hotspotKiosk = new HotspotKiosk();
    this._hotspotKiosk.setupContent(this._assetManager);
    this._scene.add(this._hotspotKiosk);
    this._hotspotKiosk.mainModel = this._mainModel!;
    this.addHotspots();

    // console.log("engineType", engineType);

    const easterEggContainer = new ThreeMeshUI.Block({
      width: 0.5,
      height: 0.045,
      padding: 0.01,
      fontFamily: this._fontPathBold,
      fontTexture: this._fontTexturePathBold,
      backgroundOpacity: 0,
      fontSize: 0.05,
    });
    // @ts-ignore This is actually a valid property
    easterEggContainer.borderRadius = 0.01;

    const text = new ThreeMeshUI.Text({
      content: "What are you looking for in here?",
      opacity: 1,
      fontColor: new THREE.Color(1, 1, 1),
    });
    if (engineType === 'W25'){
      easterEggContainer.add(text);
    }

    // container.add(new LollipopHelper(1, new THREE.Color(1, 0, 0)));


    easterEggContainer.position.set(-1.5, 1.715, -0.75);
    easterEggContainer.rotateOnAxis(new THREE.Vector3(0, 1, 0), Math.PI / 2);
    this._mainModel?.add(easterEggContainer);

    this._unicPanel.setFuelVariant("diesel");
  }

  /**
   * Load GLB models via AssetManager
   */
  private async loadGLBModels(mainModelPath: string, pointerModelPath: string): Promise<void> {
    try {

      // remove any previous models if we had preview or so

      this._floorPointer.children.forEach((child) => {
        // this._scene.remove(child);
        child.removeFromParent();
      });
      this._floorPointer.children.length = 0;

      this._previewGroup.children.forEach((child) => {
        // this._scene.remove(child);
        child.removeFromParent();
      });
      this._previewGroup.children.length = 0;

      this._floorRoot.children.forEach((child) => {
        // this._scene.remove(child);
        child.removeFromParent();
      });
      this._floorRoot.children.length = 0;
      // if (this._floorPointer){

      // }
      // this._previewGroup.children.forEach((child) => {
      //   // this._scene.remove(child);
      // });
      // this._previewGroup.children.length = 0;

      // console.log("🔄 Loading GLBs model via AssetManager...");

      let pointerModel = await this._assetManager.loadGLBModel(
        // "./global/W25_Placement.glb"
        pointerModelPath
      );


      if (pointerModel) {
        pointerModel = pointerModel.clone();
        this._floorPointer.add(pointerModel);
      }

      this._mainModel = await this._assetManager.loadGLBModel(
        // "./global/W25_DF_Genset_boolean.glb"
        mainModelPath
      );


      if (this._mainModel) {
        this._mainModel = this._mainModel.clone();

        
        this._mainModel.traverse((child) => {
          switch (child.name) {
            case "W25_DF":
              // dieselVariant = child;
              // this is fine, the default one
              // console.log("Diesel variant found");
              break;
            case "W25_LNG":
              child.visible = false;
              // console.log("LNG variant found");
              break;
            case "W25_Ammonia":
              child.visible = false;
              // console.log("Ammonia variant found");
              break;
            case "W25DF_Gas_System":
              child.visible = false;
              break;
          }
        });
        // console.log("Reassigned main model");
        // console.log("✅ Main GLB model loaded successfully via AssetManager");
        this._floorRoot.add(this._mainModel);

        // this way you can somewhat test the W31
        // this._floorRoot.scale.setScalar(0.5)
        // this._floorRoot.position.z = 3;
        // this._floorRoot.position.y = -2.5;
        // this._mainModel.rotateY(-Math.PI / 2)
        this._scene.add(this._floorRoot);
        
        this._floorRoot.visible = false;

        // Clone model for preview mode
        const previewModel = this._mainModel.clone();

        // Calculate bounding box and center the model
        const box = new THREE.Box3().setFromObject(previewModel);
        const center = box.getCenter(new THREE.Vector3());

        // Offset the model so its bounding box center is at the scene origin
        previewModel.position.set(-center.x, -center.y, -center.z);
        // const gasSystem = this._mainModel?.getObjectByName('');

        this._previewGroup.add(previewModel);
        this._previewGroup.position.set(0, 0, 0);

        // console.log("✅ Preview model added to scene (centered at origin)");
        // console.log("Model bounding box center offset:", center);
      }

    } catch (error) {
      console.error("Failed to load GLB model:", error);
    }
  }

  /**
   * Detect and setup XR cameras for proper forward direction tracking
   */
  private detectXRCameras(): void {
    try {
      const xrCamera = this._renderer.xr.getCamera();
      if (xrCamera && xrCamera.cameras && xrCamera.cameras.length >= 2) {
        this._xrCameras.leftCamera = xrCamera.cameras[0];
        this._xrCameras.rightCamera = xrCamera.cameras[1];

        console.log("XR Cameras detected:", {
          leftCamera: !!this._xrCameras.leftCamera,
          rightCamera: !!this._xrCameras.rightCamera,
        });

        // Show the floating UI when cameras are detected
        if (this._uiVisible) {
          this._helperUI.visible = true;
        }
      } else {
        console.warn("XR cameras not properly detected");
        // Try again in next frame
        setTimeout(() => this.detectXRCameras(), 100);
      }
    } catch (error) {
      console.error("Error detecting XR cameras:", error);
      // Try again in next frame
      setTimeout(() => this.detectXRCameras(), 100);
    }
  }

  /**
   * Update 3D UI positioning when XR session is active
   */
  private update3DUIPosition(): void {
    if (this._xrSession === null || !this._helperUI.visible) {
      return;
    }

    // Use the left camera as reference (both eyes should be similar)
    if (this._xrCameras.leftCamera) {
      // Get camera world position and rotation
      const cameraPosition = new THREE.Vector3();
      const cameraQuaternion = new THREE.Quaternion();
      const cameraScale = new THREE.Vector3();

      this._xrCameras.leftCamera.matrixWorld.decompose(
        cameraPosition,
        cameraQuaternion,
        cameraScale
      );

      // Store the head level Y position (only set once when first detected)
      if (!this._headLevelY) {
        this._headLevelY = cameraPosition.y;
        console.log(`Head level Y set to: ${this._headLevelY.toFixed(2)}`);
      }

      // Create a horizontal forward vector (ignore pitch/roll, only use yaw)
      const horizontalForward = new THREE.Vector3(0, 0, -1);
      // Extract only the Y-axis rotation from the camera quaternion
      const yawQuaternion = new THREE.Quaternion().setFromEuler(
        new THREE.Euler(
          0,
          Math.atan2(
            2 *
            (cameraQuaternion.w * cameraQuaternion.y +
              cameraQuaternion.x * cameraQuaternion.z),
            1 -
            2 *
            (cameraQuaternion.y * cameraQuaternion.y +
              cameraQuaternion.z * cameraQuaternion.z)
          ),
          0
        )
      );
      horizontalForward.applyQuaternion(yawQuaternion);

      // Create a left vector perpendicular to the forward vector
      const horizontalLeft = new THREE.Vector3(-1, 0, 0);
      horizontalLeft.applyQuaternion(yawQuaternion);

      // Set UI position at head level with horizontal tracking and left offset
      this._helperUI.position.set(
        cameraPosition.x +
        horizontalForward.x * this._uiDistance +
        horizontalLeft.x * this._uiLeftOffset,
        this._headLevelY, // Keep at fixed head level
        cameraPosition.z +
        horizontalForward.z * this._uiDistance +
        horizontalLeft.z * this._uiLeftOffset
      );

      // Make UI face the user horizontally (only look at X,Z position, not Y)
      const lookAtPosition = new THREE.Vector3(
        cameraPosition.x,
        this._headLevelY,
        cameraPosition.z
      );
      this._helperUI.lookAt(lookAtPosition);
    }
  }

  /**
   * Set the left offset for ContentUI positioning
   * @param offset - The left offset value (negative values move UI more to the left)
   */
  setUILeftOffset(offset: number): void {
    this._uiLeftOffset = offset;
    console.log(`ContentUI left offset set to: ${offset}`);
  }

  /**
   * Get the current left offset for ContentUI positioning
   * @returns The current left offset value
   */
  getUILeftOffset(): number {
    return this._uiLeftOffset;
  }

  /**
   * Handle controller connection
   */
  private handleControllerConnected(event: any): void {
    const controller = event.target;

    // Only create hit test targets for controllers that were set up in setupController()
    // This ensures we only respond to actual controllers, not hands
    // if (controller === this.controller || controller === this.rightController) {

    if (event.data.hand === null) {
      // this is the only way z to know if it's a controller
      this.connectedControllers.add(controller);
      // Note: Controllers are NOT added to _controllers array - only right hand index finger is used for button interaction
      console.log("Controller connected:", event.data || "unknown");

      if (event.data?.handedness === "right") {
        this._rightInputSource = event.data;
        this.reality
          .createHitTestTargetFromControllerSpace(
            event.data?.handedness || "right"
          )
          .then((hitTestTarget: HitTestTarget) => {
            (controller as any).hitTestTarget = hitTestTarget;
            this._activeHitTestController = controller;
          })
          .catch((error) => {
            console.error("Could not start controller hit testing.", error);
          });
      }
    } else {
      // console.log("Hand input detected, skipping hit test target creation");
    }
  }

  /**
   * Handle controller disconnection
   */
  private handleControllerDisconnected(event: any): void {
    const controller = event.target;
    this.connectedControllers.delete(controller);

    // Note: Controllers are NOT removed from _controllers array - only right hand index finger is used for button interaction

    // console.log("Controller disconnected");

    if (event.data.hand === null) {
      if (event.data?.handedness === "right") {
        this._rightInputSource = null;
        this.reality.deleteHitTestTarget((controller as any).hitTestTarget);
        (controller as any).hitTestTarget = null;
        this._activeHitTestController = null;
      } else {
        this._pendingHeightOffset = 0;
      }
    }
  }

  /**
   * Handle grip action to rotate hit test target
   * Each grip adds 90 degrees to the Y-axis rotation
   * Only responds to grip from the controller actively doing hit testing
   */
  private handleSqueezeStart(event: any): void {
    const triggeringController = event.target;

    // Only respond to actual connected controllers, not hand tracking
    if (!this.connectedControllers.has(triggeringController)) {
      // console.log("Grip event ignored - not from a connected controller");
      return;
    }
    // Only respond to grip from the controller actively doing hit testing
    // This allows the secondary controller's buttons to be used for other actions
    if (event.data?.handedness === "right") {

      // Increment grip rotation offset by 90 degrees
      // this._gripRotationOffset += Math.PI / 2;
      this._rotationOffset += degToRad(15);
      // Normalize to 0-360 degrees (0-2π radians)
      this._rotationOffset = this._rotationOffset % (2 * Math.PI);

      console.log(
        `Grip action detected - Y-axis rotation offset: ${(
          (this._rotationOffset * 180) /
          Math.PI
        ).toFixed(1)}°`
      );

      // Apply the cumulative rotation to the hit test target
      this.applyGripRotation();
      return;
    } else {
      this._pendingHeightOffset = this._pendingHeightOffsetSpeed;
    }
  }


  /**
   * Handle grip action to rotate hit test target
   * Each grip adds 90 degrees to the Y-axis rotation
   * Only responds to grip from the controller actively doing hit testing
   */
  private handleSqueezeEnd(event: any): void {
    const triggeringController = event.target;

    // Only respond to actual connected controllers, not hand tracking
    if (!this.connectedControllers.has(triggeringController)) {
      // console.log("Grip event ignored - not from a connected controller");
      return;
    }
    // Only respond to grip from the controller actively doing hit testing
    // This allows the secondary controller's buttons to be used for other actions
    if (event.data?.handedness === "right") {
      return;
    } else {
      this._pendingHeightOffset = 0;
    }
  }


  /**
   * Hold the right thumbstick sideways to yaw the placement target.
   * Grip still adds a fixed step on top of this.
   */
  private updateThumbstickRotation(deltaTime: number): void {
    const gamepad = this._rightInputSource?.gamepad;
    if (!gamepad) return;

    const stickX = this.readThumbstickX(gamepad);
    if (Math.abs(stickX) < this._thumbstickDeadzone) return;

    const deflection =
      (Math.abs(stickX) - this._thumbstickDeadzone) / (1 - this._thumbstickDeadzone);
    this._rotationOffset +=
      Math.sign(stickX) * deflection * this._thumbstickYawSpeed * deltaTime;
    this._rotationOffset = this._rotationOffset % (2 * Math.PI);
    this.applyGripRotation();
  }

  private readThumbstickX(gamepad: Gamepad): number {
    const axes = gamepad.axes;
    if (axes.length < 2) return 0;
    const primary = axes[0] ?? 0;
    const alternate = axes.length > 2 ? axes[2] ?? 0 : 0;
    return Math.abs(alternate) > Math.abs(primary) ? alternate : primary;
  }

  /**
   * Apply grip rotation offset to the hit test target
   */
  private applyGripRotation(): void {
    if (this._lastHitTestTarget) {
      // Start with base rotation
      this._lastHitTestTarget.rotation.copy(this._baseRotation);

      // Apply grip rotation offset on Y-axis
      this._lastHitTestTarget.rotateOnAxis(
        new THREE.Vector3(0, 1, 0),
        this._rotationOffset
      );

      // Update matrices
      this._lastHitTestTarget.updateMatrix();
      this._lastHitTestTarget.updateMatrixWorld();
    }
  }

  /**
   * Reset grip rotation offset
   */
  private resetGripRotation(): void {
    this._rotationOffset = 0;
    console.log("Grip rotation reset to 0°");
    this.applyGripRotation();
  }

  /**
   * Public method to reset grip rotation (for external control)
   */
  public resetHitTargetRotation(): void {
    this.resetGripRotation();
  }

  /**
   * Public method to get current grip rotation offset in degrees
   */
  public getGripRotationOffset(): number {
    return (this._rotationOffset * 180) / Math.PI;
  }

  /**
   * Public method to manually set grip rotation offset in degrees
   */
  public setGripRotationOffset(degrees: number): void {
    this._rotationOffset = (degrees * Math.PI) / 180;
    this._rotationOffset = this._rotationOffset % (2 * Math.PI);
    console.log(`Manual grip rotation set to: ${degrees}°`);
    this.applyGripRotation();
  }

  /**
   * Handle controller selection start with direction alignment check
   * Only responds to explicit controller button presses, not hand gestures
   */
  private handleSelectStart(event: any): void {
    // Get the controller that triggered the event
    const triggeringController = event.target;

    // Only respond to actual connected controllers, not hand tracking
    if (!this.connectedControllers.has(triggeringController)) {
      console.log("Select event ignored - not from a connected controller");
      return;
    }


    if (event.data?.handedness === "right") {
      return;
    } else {

      this._pendingHeightOffset = -this._pendingHeightOffsetSpeed;
    }

  }


  /**
   * Handle controller selection start with direction alignment check
   * Only responds to explicit controller button presses, not hand gestures
   */
  private handleSelectEnd(event: any): void {
    // Get the controller that triggered the event
    const triggeringController = event.target;

    // Only respond to actual connected controllers, not hand tracking
    if (!this.connectedControllers.has(triggeringController)) {
      console.log("Select event ignored - not from a connected controller");
      return;
    }


    if (event.data?.handedness === "right") {

      // check and use the stored and adjusted value from last hit
      if (this._lastHitTestTarget) {

        // Check if camera direction and controller direction are aligned
        const isDirectionAligned =
          this.checkCameraControllerAlignment(triggeringController);

        if (isDirectionAligned) {
          this.pendingAnchorData = {
            position: this._lastHitTestTarget.position.clone(),
            quaternion: this._lastHitTestTarget.quaternion.clone(),
          };
          console.log(
            "Direction confirmed - selection point set at:",
            this._lastHitTestTarget.position
          );
        } else {
          console.log(
            "Misaligned directions - select event ignored (frustum test failed)"
          );
        }
      }
    } else {

      this._pendingHeightOffset = 0;
    }

  }


  /**
   * Check if the hit test target position is inside the left XR camera frustum
   * Uses XR headset camera frustum for VR accuracy
   */
  private checkCameraControllerAlignment(controller?: any): boolean {
    const targetController = controller || this.primaryController;
    if (!targetController) {
      return false;
    }

    // Get the hit test target position
    const hitTestTarget = (targetController as any).hitTestTarget;
    if (!hitTestTarget) {
      return false;
    }

    // Use XR camera if available, otherwise fall back to browser camera
    if (this._xrCameras.leftCamera) {
      // Use first XR camera (left eye) for frustum test
      // Check if hit test position is inside the camera frustum
      const frustum = new THREE.Frustum();
      const matrix = new THREE.Matrix4().multiplyMatrices(
        this._xrCameras.leftCamera.projectionMatrix,
        this._xrCameras.leftCamera.matrixWorldInverse
      );
      frustum.setFromProjectionMatrix(matrix);

      const isInsideFrustum = frustum.containsPoint(hitTestTarget.position);

      //   console.log(`HitTest position: [${hitTestTarget.position.x.toFixed(2)}, ${hitTestTarget.position.y.toFixed(2)}, ${hitTestTarget.position.z.toFixed(2)}] || Inside frustum: ${isInsideFrustum}`);

      return isInsideFrustum;
    } else {
      // Fallback to browser camera frustum test (not ideal for VR)
      const frustum = new THREE.Frustum();
      const matrix = new THREE.Matrix4().multiplyMatrices(
        this.camera.projectionMatrix,
        this.camera.matrixWorldInverse
      );
      frustum.setFromProjectionMatrix(matrix);

      const isInsideFrustum = frustum.containsPoint(hitTestTarget.position);

      //   console.log(`HitTest position: [${hitTestTarget.position.x.toFixed(2)}, ${hitTestTarget.position.y.toFixed(2)}, ${hitTestTarget.position.z.toFixed(2)}] || Inside browser camera frustum: ${isInsideFrustum}`);

      return isInsideFrustum;
    }
  }

  /**
   * Keep the lowest detected plane as the floor used for placement.
   */
  private handlePlaneAdded(plane: FloorPlane): void {
    const mesh = plane.planeMesh;
    mesh.material = new MeshBasicMaterial({
      //   wireframe: true,
      color: 0x00ff00,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.33,
      visible: false,
    });
    // mesh.add(new AxesHelper(1));
    if (this.lowestPlane === undefined) {
      this.lowestPlane = plane;
    } else if (
      plane.planeMesh.parent!.position.y <
      this.lowestPlane?.planeMesh.parent!.position.y
    ) {
      this.lowestPlane = plane;
    }
    this._planes.push(plane);
    // it seems that only the last plane is in the scene and can be seen

    // this.updatePlaneVisibilities(true);
    // this._scene.add(mesh.parent!);
  }

  private handlePlaneRemoved(plane: FloorPlane): void {
    this._planes = this._planes.filter((entry) => entry !== plane);
    if (this._lowestPlane !== plane) return;

    let nextLowest: FloorPlane | undefined;
    for (const candidate of this._planes) {
      if (nextLowest === undefined || candidate.position.y < nextLowest.position.y) {
        nextLowest = candidate;
      }
    }
    if (nextLowest) {
      this.lowestPlane = nextLowest;
    } else {
      this._lowestPlane = undefined;
    }
  }

  updatePlaneVisibilities(showLowest: boolean): void {
    this._planes.forEach((plane) => {
      (plane.planeMesh.material as THREE.MeshBasicMaterial).visible = false;
    });
    if (this.lowestPlane !== undefined) {
      (this.lowestPlane.planeMesh.material as THREE.MeshBasicMaterial).visible =
        showLowest;
    }
  }

  /**
   * Update hit test position based on controller pointing (both controllers)
   * Only shows when actual controllers are connected, not during hand tracking
   */
  private updateHitTesting(): void {
    if (!this.reality) return;

    // Only show hit testing when actual controllers are connected
    // Hand tracking will not trigger this visualization
    if (this.connectedControllers.size === 0) {
      this.hitTestMarker.visible = false;
      this.hitTestLine.visible = false;
      this._floorPointer.visible = false;
      if (this.lowestPlane !== undefined) {
        (
          this.lowestPlane.planeMesh.material as THREE.MeshBasicMaterial
        ).visible = false;
      }
      return;
    }

    // Check both controllers for active hit targets
    let activeController: any = null;

    let target: THREE.Group | undefined;

    // Only check controllers that are actually connected
    this.connectedControllers.forEach((controller) => {
      if (!activeController) {
        // Only check if we haven't found one yet
        const hitTarget = (controller as any).hitTestTarget as THREE.Group;
        if (hitTarget) {
          activeController = controller;
          //   this._lastHitTestTarget = hitTarget;
          target = hitTarget;
        }
      }
    });

    // Store the active hit test controller for grip action differentiation
    // this._activeHitTestController = activeController;

    // Hide hit testing when no controllers are actively pointing
    // This allows hand tracking to be used for other interactions
    if (activeController === null || target === undefined) {
      this.hitTestMarker.visible = false;
      this.hitTestLine.visible = false;
      this._floorPointer.visible = false;
      return;
    } else {
      this._lastHitTestTarget.position.copy(target.position);
      this._lastHitTestTarget.quaternion.copy(target.quaternion);
      this._lastHitTestTarget.updateMatrix();
      this._lastHitTestTarget.updateMatrixWorld();
    }

    if (this.lowestPlane !== undefined) {
      this._lastHitTestTarget.position.y =
        this.lowestPlane.planeMesh.position.y;
      this.applyStableRotationToHitTarget(this.lowestPlane.planeMesh);
      this._lastHitTestTarget.updateMatrix();
      this._lastHitTestTarget.updateMatrixWorld();
    }

    // Update hit test marker position
    this.hitTestMarker.position.copy(this._lastHitTestTarget.position);
    this.hitTestMarker.rotation.copy(this._lastHitTestTarget.rotation);
    this.hitTestMarker.updateMatrix();
    this.hitTestMarker.updateMatrixWorld();
    this.hitTestMarker.visible = true;

    // Update line from controller to hit point
    const controllerPosition = new Vector3();
    activeController.getWorldPosition(controllerPosition);

    this.hitTestLine.geometry.dispose();
    const lineGeometry = new BufferGeometry().setFromPoints([
      controllerPosition,
      this._lastHitTestTarget.position,
    ]);
    this.hitTestLine.geometry = lineGeometry;
    this.hitTestLine.visible = true;
    this._floorPointer.visible = true;
    if (this.lowestPlane !== undefined) {
      (this.lowestPlane.planeMesh.material as THREE.MeshBasicMaterial).visible =
        true;
    }
  }

  /**
   * Apply stable rotation to hit test target based on plane dimensions
   * Uses the same logic as main.ts applyStableRotation function
   */
  private applyStableRotationToHitTarget(planeMesh: THREE.Mesh): void {
    // Reset rotation
    this._lastHitTestTarget.rotation.set(0, 0, 0);

    // Get plane dimensions from the mesh geometry
    const geometry = planeMesh.geometry;
    if (!geometry.boundingBox) {
      geometry.computeBoundingBox();
    }

    const boundingBox = geometry.boundingBox!;
    const size = new THREE.Vector3();
    boundingBox.getSize(size);

    // Transform size to world coordinates to account for plane rotation
    const worldSize = new THREE.Vector3();
    worldSize.copy(size);
    worldSize.applyMatrix4(planeMesh.matrixWorld);

    // Check if plane is longer in its forward direction (Z) than wide (X)
    const isLongerInForward = Math.abs(worldSize.z) > Math.abs(worldSize.x);
    this._lastHitTestTarget.rotation.copy(planeMesh.parent!.rotation);

    // Get the up vector from the plane mesh parent in world space
    const parentUpVector = new THREE.Vector3(0, 1, 0);
    parentUpVector.applyQuaternion(planeMesh.parent!.quaternion);

    const planeDot = parentUpVector.dot(new THREE.Vector3(0, 1, 0));

    if (planeDot < 0.1) {
      // upside down
      this._lastHitTestTarget.rotateOnAxis(new THREE.Vector3(1, 0, 0), Math.PI);
    } else if (planeDot < 0.9) {
      // on it's side
      this._lastHitTestTarget.rotateOnAxis(
        new THREE.Vector3(1, 0, 0),
        Math.PI / 2
      );
    }

    // this._lastHitTestTarget.rotateOnAxis(new THREE.Vector3(1, 0, 0), Math.PI);

    // Apply 90-degree rotation if needed to align content properly
    if (isLongerInForward) {
      this._lastHitTestTarget.rotateOnAxis(
        new THREE.Vector3(0, 1, 0),
        Math.PI / 2
      );
      // console.log(
      //   "Applied 90° rotation - plane is longer in forward direction"
      // );
    } else {
      // console.log("No additional rotation needed - plane is wider than long");
    }

    // Store the base rotation (before grip adjustments)
    this._baseRotation.copy(this._lastHitTestTarget.rotation);

    // Apply any existing grip rotation offset
    this.applyGripRotation();
  }

  /**
   * Handles the creation of anchors based on pending data.
   * Only allows 1 persistent anchor at a time.
   * If an anchor already exists, moves it to the new position.
   */
  private _placingAnchor = false;
  private readonly _placementStorageKey = "wartsila-placement-offset";

  private handlePendingAnchors(): void {
    if (!this.pendingAnchorData || !this.reality || this._placingAnchor) return;

    const pending = this.pendingAnchorData;
    this.pendingAnchorData = null;

    // Another placement reuses the saved anchor. Creating a new persistent
    // anchor, or deleting the old one, appends empty ids that Quest cannot remove.
    if (this.currentAnchor) {
      this.movePlacement(this.currentAnchor, pending);
      return;
    }

    this._placingAnchor = true;
    // createAnchor must run in this animation frame, before any await.
    void this.reality
      .createAnchor(pending.position, pending.quaternion, true)
      .then((anchor) => {
        this.currentAnchor = anchor;
        this._floorRoot.position.set(0, 0, 0);
        this._floorRoot.quaternion.identity();
        anchor.add(this._floorRoot);
        this._floorRoot.visible = true;
        this.savePlacementOffset(anchor.anchorID);
        console.log("Anchor created at position:", anchor.position);
      })
      .catch((error) => {
        console.error("Failed to create anchor:", error);
      })
      .finally(() => {
        this._placingAnchor = false;
      });
  }

  private movePlacement(anchor: PlacementAnchor, pending: PendingAnchorData): void {
    if (this._floorRoot.parent !== anchor) {
      anchor.add(this._floorRoot);
    }
    const inverse = anchor.quaternion.clone().invert();
    this._floorRoot.position.copy(pending.position).sub(anchor.position).applyQuaternion(inverse);
    this._floorRoot.quaternion.copy(inverse.multiply(pending.quaternion));
    this._floorRoot.visible = true;
    this.savePlacementOffset(anchor.anchorID);
    console.log("Placement moved to:", pending.position);
  }

  private savePlacementOffset(anchorId: string | undefined): void {
    if (!anchorId) return;
    const position = this._floorRoot.position;
    const quaternion = this._floorRoot.quaternion;
    try {
      localStorage.setItem(
        this._placementStorageKey,
        JSON.stringify({
          anchorId,
          position: [position.x, position.y, position.z],
          quaternion: [quaternion.x, quaternion.y, quaternion.z, quaternion.w],
        }),
      );
    } catch {
      // Private browsing can refuse storage. The placement still shows this session.
    }
  }

  private applySavedPlacementOffset(anchorId: string | undefined): void {
    if (!anchorId) return;
    try {
      const raw = localStorage.getItem(this._placementStorageKey);
      if (!raw) return;
      const saved = JSON.parse(raw) as {
        anchorId?: string;
        position?: number[];
        quaternion?: number[];
      };
      if (saved.anchorId !== anchorId || !saved.position || !saved.quaternion) return;
      this._floorRoot.position.fromArray(saved.position);
      this._floorRoot.quaternion.fromArray(saved.quaternion);
    } catch {
      // Ignore unreadable saved data and keep the anchor pose.
    }
  }

  update() {
    // Update 3D UI positioning when XR session is active
    this.update3DUIPosition();

    const deltaTime = this._clock.getDelta();
    this.updateThumbstickRotation(deltaTime);

    if (this._elevatorTarget !== undefined){
      this._elevatorPhase += deltaTime * this._elevatorSpeed * this._elevatorDirection;
      this._elevatorPhase = THREE.MathUtils.clamp(this._elevatorPhase, 0, 1);
      // this._elevatorPhase = EasingFunction.easeInCubic(this._elevatorPhase);
      // const travelPhase = 
      // const newY = this._elevatorStartY + THREE.MathUtils.lerp(0, this._elevatorTraverse, this._elevatorPhase);
      // this._elevatorTarget.position.z = newY; // local rotation of the engine is around the z axis
      const eased = EasingFunction.easeInOutCubic(this._elevatorPhase);
      this._elevatorTarget.position.z = THREE.MathUtils.lerp(this._elevatorStartY, this._elevatorStartY + this._elevatorTraverse, eased);
    }


    // Update preview mode rotation
    if (this._previewMode && this._previewGroup.visible) {
      this._previewGroup.rotation.y += this._modelRotationSpeed * deltaTime;
    }
    if (this._xrCameras.leftCamera && this._xrCameras.rightCamera) {
      this._xrCameras.midPoint.setScalar(0);
      this._xrCameras.midPoint.add(this._xrCameras.leftCamera.position);
      this._xrCameras.midPoint.add(this._xrCameras.rightCamera.position);
      this._xrCameras.midPoint.divideScalar(2);
      for (const hotspot of this._hotspots.values()) {
        hotspot.update(this._xrCameras.midPoint, deltaTime);
      }
      this._audioListener.position.copy(this._xrCameras.midPoint);
      this._audioListener.rotation.copy(this._xrCameras.leftCamera.rotation);
      this._audioListener.rotateOnAxis(new THREE.Vector3(0, 1, 0), Math.PI);
    }

    this._helperUI.update();

    // check if we are touching or close to buttons
    for (const button of this._buttons) {
      let isTouched = false;
      for (const controller of this._controllers) {
        isTouched =
          controller
            .getWorldPosition(new THREE.Vector3())
            .distanceTo(button.getWorldPosition(new THREE.Vector3())) <
          button.touchDistance;
        if (isTouched) {
          break;
        }
      }

      button.isTouched = isTouched;
      button.updateDelta(deltaTime);
    }

    // Update hit testing
    this.updateHitTesting();
    // Handle anchor creation for pending selection points
    this.handlePendingAnchors();

    if (this._unicPanel) {
      this._unicPanel.update(deltaTime);
    }

    if (this._flywheel !== null) {
      this._flywheel.rotation.y -= deltaTime * this._flywheelSpeed * this._flywheelSpeedMultiplier;
    }

    if (this._hotboxHatch.target !== undefined) {
      this._hotboxHatch.animation.phase += deltaTime / this._hotboxHatch.animation.duration * (this._hotboxHatch.state === 'open' ? 1 : -1);
      this._hotboxHatch.animation.phase = THREE.MathUtils.clamp(this._hotboxHatch.animation.phase, 0, 1);
      this._hotboxHatch.target.rotation.y = this._hotboxHatch.animation.easing(this._hotboxHatch.animation.phase) * this._hotboxHatch.animation.openRotation;
    }

    this._floorRoot.position.y += this._pendingHeightOffset * deltaTime;
    if (this._pendingHeightOffset !== 0) {
      this.savePlacementOffset(this.currentAnchor?.anchorID);
    }

    // this._floorRoot.position.y = -2.5

    // if (this._pendingHeightOffset !== 0){

    // }

    if (this.reality) {
      this.reality.update();
    }
    // (this._scene as any).environmentRotation.y += deltaTime * 10;

    // const floorWorldQuat = this._floorRoot.getWorldQuaternion(new THREE.Quaternion());
    // const sceneRotation = new THREE.Euler().setFromQuaternion(floorWorldQuat);
    // sceneRotation.y += this._environmentOffset;
    // (this._scene as any).environmentRotation.copy(sceneRotation);

    if (this._renderer !== undefined) {
      this._renderer.render(this._scene, this.camera);
    }
  }

}

// Initialize the RATK application
// new RATKApp(EngineType.W31);
